import { isFormatTag, prepareDeckEvent } from "@/lib/nostr/deck";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

import { NostrEvent } from "@nostrify/nostrify";
import { useNostrPublish } from "@/hooks/useNostrPublish";
import { useQuery } from "@tanstack/react-query";
import { ScrollArea } from "./ui/scroll-area";
import { ScryfallCard, ScryfallCardWithCount } from "@/types/scryfall";
import { Switch } from "./ui/switch";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { MTGCard } from "./MTGCard";

interface ChangeArtDialogProps {
  card: ScryfallCardWithCount;
  cards: ScryfallCardWithCount[];
  listEvent: NostrEvent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const fetchPrints = async (prints_search_uri: string): Promise<ScryfallCard[]> => {
  const response = await fetch(prints_search_uri);
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  const data = await response.json();
  return data.data;
};

export function ChangeArtDialog({ card, cards, listEvent, open, onOpenChange }: ChangeArtDialogProps) {
  const { mutate: publish } = useNostrPublish();
  const [isFoil, setIsFoil] = useState(card.is_foil === 'foil');

  const { data: prints, isLoading } = useQuery({
    queryKey: ['prints', card.prints_search_uri],
    queryFn: () => fetchPrints(card.prints_search_uri),
    enabled: !!card.prints_search_uri,
  });

  const handleArtChange = (newCard: ScryfallCard) => {
    if (!listEvent) return;

    const quantity = card.count;

    const otherCards = cards.filter(c => c.id !== card.id);
    const updatedCards = [...otherCards, { ...newCard, count: quantity, is_foil: isFoil ? 'foil' : '' }];

    const title = listEvent.tags.find(t => t[0] === 'title')?.[1] || 'Untitled Deck';
    const format = listEvent.tags.find(isFormatTag)?.[1] || '';
    let commanderCardNames = listEvent.tags.filter(t => t[0] === 'C').map(t => t[1]);
    const companionCardName = listEvent.tags.find(t => t[0] === 'S')?.[1] || null;

    if (commanderCardNames.includes(card.name)) {
        commanderCardNames = commanderCardNames.filter(c => c !== card.name);
        commanderCardNames.push(newCard.name);
    }

    const eventToPublish = prepareDeckEvent(
      { event: listEvent, title, cards: updatedCards.map(c => ({ name: c.name, quantity: c.count || 1})), sideboard: [], commanders: commanderCardNames, companion: companionCardName || undefined },
      title,
      format,
      updatedCards,
      [],
      commanderCardNames,
      companionCardName
    );

    publish(eventToPublish, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Change Art for {card.name}</DialogTitle>
          <DialogDescription>Select a new printing for your card.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center space-x-2 my-4">
          <Switch id="foil-switch" checked={isFoil} onCheckedChange={setIsFoil} />
          <Label htmlFor="foil-switch">Foil</Label>
        </div>
        <ScrollArea className="h-96">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4 p-1">
            {isLoading && Array.from({ length: 10 }).map((_, i) => <div key={i} className="h-full aspect-[5/7] bg-gray-200 rounded-lg" />)}
            {prints?.map(p => (
              <div key={p.id} className="cursor-pointer" onClick={() => handleArtChange(p)}>
                <MTGCard
                  card={{...p, is_foil: isFoil ? 'foil' : ''}}
                  enable3D={true}
                  showCount={false}
                />
              </div>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
